import React, { useCallback, useEffect, useState } from "react";
import {
  BookOpen,
  BarChart3,
  FileText,
  Settings,
} from "lucide-react";

import Sidebar from "./Sidebar";
import Header from "./Header";
import Dashboard from "./Dashboard";
import Profile from "./Profile";
import Results from "./Results";
import Courses from "./Courses";
import Placeholder from "./Placeholder";
import AcademicPerformance from "./AcademicPerformance";
import Setting from "./Settings";

import { stats } from "../studentData";
import { useAuth } from "../context/AuthContext";
import TokenService from "../services/tokenService";

import {
  fetchStudentRecord,
  fetchStudentPhoto,
} from "../services/studentService";

const PANEL_COPY = {
  courses: {
    icon: BookOpen,
    title: "Courses",
    message:
      "Your enrolled courses for this semester will appear here once registration opens.",
  },

  performance: {
    icon: BarChart3,
    title: "Academic Performance",
    message:
      "Grade trends and CGPA history will be shown here after your first results are published.",
  },

  results: {
    icon: FileText,
    title: "Results",
    message:
      "Semester results will be listed here as soon as they are released by the registrar.",
  },

  settings: {
    icon: Settings,
    title: "Settings",
    message:
      "Account, notification and password preferences will live here.",
  },
};

export default function ProtectedApp() {
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [student, setStudent] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = TokenService.getAccessToken();

  /**
   * Get student ID from authenticated user.
   */
  /*const studentId =
    user?.studentId ??
    user?.student_id ??
    user?.studentID ??
    user?.id ??
    user?.loginId ??
    user?.login_id ??
    null;*/

  /**
   * Get student name safely.
   */
  const studentName =
    student?.per_name ??
    student?.name ??
    student?.fullName ??
    student?.full_name ??
    user?.per_name ??
    user?.name ??
    user?.fullName ??
    user?.full_name ??
    user?.studentName ??
    user?.username ??
    "";

  /**
   * Responsive sidebar.
   */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * Handle navigation.
   */
  const handleNavigate = async (key) => {
    if (key !== "logout") {
      setActiveTab(key);
      setSidebarOpen(false);
      return;
    }

    if (loggingOut) return;

    try {
      setLoggingOut(true);
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  /**
   * Load student profile and photo.
   */
  const loadProfile = useCallback(async () => {
    if (!user || !token) {
      setStudent(null);
      setPhoto(null);
      setError(
        "Student information or authentication token is missing."
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setPhoto(null);
    setPhotoError(false);

    try {
      /**
       * Load student information.
       */
      const record = await fetchStudentRecord(
        user,
        token
      );

      if (!record) {
        throw new Error(
          "Student profile information was not found."
        );
      }

      setStudent(record);

      /**
       * Load student photo separately.
       *
       * Photo failure should not prevent
       * student information from displaying.
       */
      try {
        const photoBlobUrl = await fetchStudentPhoto(
          user,
          token
        );

        if (photoBlobUrl) {
          setPhoto(photoBlobUrl);
          setPhotoError(false);
        } else {
          setPhoto(null);
          setPhotoError(true);
        }
      } catch (photoErr) {
        setPhoto(null);
        setPhotoError(true);

        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[ProtectedApp] Student photo could not be loaded.",
            photoErr
          );
        }
      }
    } catch (err) {
      const status = err?.response?.status;

      if (status === 401) {
        setError(
          "Your session has expired. Please log in again."
        );
      } else if (status === 403) {
        setError(
          "You do not have permission to view this profile."
        );
      } else if (status === 404) {
        setError(
          "Student profile information was not found."
        );
      } else if (status >= 500) {
        setError(
          "The server is currently unavailable. Please try again later."
        );
      } else if (
        err?.message?.toLowerCase().includes("network") ||
        !err?.response
      ) {
        setError(
          "Unable to connect to the server. Please check your internet connection and try again."
        );
      } else {
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.message ||
            "Unable to load student profile."
        );
      }

      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[ProtectedApp] Failed to load student profile.",
          status ? `HTTP ${status}` : "Request failed",
          err
        );
      }
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  /**
   * Load profile when student ID or token changes.
   */
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  /**
   * Render active panel.
   */
  const renderPanel = () => {
    if (activeTab === "dashboard") {
      return (
        <Dashboard
          stats={stats}
          student={student || user}
        />
      );
    }

    if (activeTab === "profile") {
      return (
        <Profile
          studentId={user}
          student={student}
          photo={photo}
          photoError={photoError}
        />
      );
    }

    if (activeTab === "results") {
      return (
        <Results
          student_id={user}
          token={token}
        />
      );
    }

    if (activeTab === "courses") {
      return (
        <Courses
          student_id={user}
          token={token}
        />
      );
    }

    if (activeTab === "performance") {
      return (
        <AcademicPerformance
          student_id={user}
          token={token}
        />
      );
    }

    if (activeTab === "settings") {
      return (
        <Setting
          student_id={user}
          token={token}
        />
      );
    }

    const copy = PANEL_COPY[activeTab];

    return copy ? <Placeholder {...copy} /> : null;
  };

  /**
   * Authentication guard.
   */
  if (!user) {
    return null;
  }

  /**
   * Student ID guard.
   */
  if (!user) {
    return (
      <div className="app-shell">
        <div className="app-main">
          <main className="app-content">
            <div className="profile-error">
              {error ||
                "Student ID could not be found in your account information."}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar
        activeTab={activeTab}
        onNavigate={handleNavigate}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="app-main">
        <Header
          studentId={user}
          studentName={studentName}
          onOpenMenu={() => setSidebarOpen(true)}
          onLogout={() => handleNavigate("logout")}
        />

        <main className="app-content">
          {loading && !student ? (
            <div className="profile-loading">
              <div className="profile-spinner" />

              <h3>Loading Student Information</h3>

              <p>
                Please wait while we load your student
                information...
              </p>
            </div>
          ) : error && !student ? (
            <div className="profile-error">
              {error}
            </div>
          ) : (
            renderPanel()
          )}
        </main>

        <footer className="app-footer">
          &copy; {new Date().getFullYear()} Metropolitan University.
          All rights reserved.
        </footer>
      </div>

      {loggingOut && (
        <div className="logout-overlay">
          <div className="logout-dialog">
            Signing out…
          </div>
        </div>
      )}
    </div>
  );
}